<template>
  <nav
    class="navbar py-3"
    :class="[
      isAgencyPortal ? 'is-agency-nav' : 'has-background-light',
      { 'has-brand-mark': isAgencyPortal || isNexus },
    ]"
    v-if="user && user.id != null"
  >
    <div class="container is-fluid">
      <!-- Mobile: logo left, user avatar right -->
      <div class="navbar-brand-mobile is-hidden-tablet">
        <router-link :to="logoLinkDestination" class="navbar-item">
          <img
            :src="currentLogo"
            :alt="isNexus ? 'Anthem Logo' : companyName"
            class="logo-image"
            :class="{ 'is-svg': isCurrentLogoSvg }"
            @error="handleLogoError"
            @load="handleLogoLoad"
          />
        </router-link>
        <div class="mobile-user-menu">
          <div
            class="dropdown is-right"
            :class="{ 'is-active': isMobileUserMenuOpen }"
          >
            <div class="dropdown-trigger" @click.stop="toggleMobileUserMenu">
              <div class="mobile-avatar-wrapper">
                <UserAvatar :user="user" size="medium" no-tooltip />
                <span v-if="unreadNotificationCount > 0" class="avatar-badge">
                  {{ unreadNotificationCount > 9 ? '9+' : unreadNotificationCount }}
                </span>
              </div>
            </div>
            <div class="dropdown-menu" role="menu">
              <div class="dropdown-content">
                <router-link
                  class="dropdown-item"
                  :to="{ name: profileRouteName }"
                  @click="closeMobileUserMenu"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'user']" />
                  </span>
                  <span>My Profile</span>
                </router-link>
                <div
                  class="dropdown-item"
                  @click="openMobileNotifications(); closeMobileUserMenu();"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'bell']" />
                  </span>
                  <span>Notifications</span>
                  <span v-if="unreadNotificationCount > 0" class="tag is-danger is-rounded ml-auto" style="font-size: 0.65rem; height: 1.25em; padding: 0 0.4em;">
                    {{ unreadNotificationCount > 9 ? '9+' : unreadNotificationCount }}
                  </span>
                </div>
                <div
                  v-if="isAdminUser"
                  class="dropdown-item"
                  @click="openCompanySwitcher(); closeMobileUserMenu();"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'person-to-portal']" />
                  </span>
                  <span>{{ isNexus ? 'Log into Company' : 'Switch Company' }}</span>
                </div>
                <div
                  v-if="hasMultipleAccounts"
                  class="dropdown-item"
                  @click="handleSwitchAccount(); closeMobileUserMenu();"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'people-arrows']" />
                  </span>
                  <span>Switch Account</span>
                </div>
                <hr class="dropdown-divider" />
                <div class="dropdown-item">
                  <button
                    class="button is-danger is-fullwidth is-small"
                    @click="logOut(); closeMobileUserMenu();"
                  >
                    <span class="icon">
                      <font-awesome-icon :icon="['fal', 'arrow-right-from-bracket']" />
                    </span>
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop: centered logo -->
      <div class="navbar-brand is-hidden-mobile">
        <router-link :to="logoLinkDestination" class="navbar-item">
          <img
            :src="currentLogo"
            :alt="isNexus ? 'Anthem Logo' : companyName"
            class="logo-image"
            :class="{ 'is-svg': isCurrentLogoSvg }"
            @error="handleLogoError"
            @load="handleLogoLoad"
          />
          <!-- Mode marker. The brandmark alone says "Vysual"; this says WHICH
               Vysual you're in. Kept to a typeset label rather than a second
               logo so it reads as a mode, not a brand. -->
          <span v-if="modeMarker" class="mode-marker">{{ modeMarker }}</span>
        </router-link>
      </div>

      <!-- Desktop Navigation -->
      <div class="navbar-menu is-hidden-mobile">
        <div class="navbar-start">
          <AdminDesktopNav v-if="isNexus" />
          <AgencyDesktopNav v-else-if="isAgencyPortal" />
          <ClientDesktopNav v-else-if="showClientNav" />
        </div>

        <NavbarUserControls />
      </div>
    </div>

    <!-- Mobile Bottom Navigation + Modals -->
    <NavbarMobileNav />

    <!-- One instance for both desktop and mobile — it teleports to <body>. -->
    <CompanySpotlight
      :open="isCompanySwitcherOpen"
      :companies="availableCompanies"
      :current-id="viewModeStore.impersonatedCompany"
      :is-nexus="isNexus"
      @select="handleCompanySelect"
      @close="closeCompanySwitcher"
      @stop-impersonation="handleStopImpersonation"
    />
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNavbarState } from '@/composables/useNavbarState'
import AdminDesktopNav from '@/components/global/nav/AdminDesktopNav.vue'
import AgencyDesktopNav from '@/components/global/nav/AgencyDesktopNav.vue'
import ClientDesktopNav from '@/components/global/nav/ClientDesktopNav.vue'
import NavbarUserControls from '@/components/global/nav/NavbarUserControls.vue'
import NavbarMobileNav from '@/components/global/nav/NavbarMobileNav.vue'
import CompanySpotlight from '@/components/global/nav/CompanySpotlight.vue'
import UserAvatar from '@/components/common/UserAvatar.vue'

const emit = defineEmits(['navbar-mounted'])

const {
  user,
  isNexus,
  isAgencyPortal,
  showClientNav,
  logoLinkDestination,
  currentLogo,
  modeMarker,
  isCurrentLogoSvg,
  companyName,
  handleLogoLoad,
  handleLogoError,
  unreadNotificationCount,
  profileRouteName,
  openMobileNotifications,
  openCompanySwitcher,
  closeCompanySwitcher,
  isCompanySwitcherOpen,
  availableCompanies,
  handleCompanySelect,
  handleStopImpersonation,
  viewModeStore,
  handleSwitchAccount,
  logOut,
  userAuthStore,
} = useNavbarState()

// Derive directly from userAuthStore to avoid inject timing edge-cases.
const isAdminUser = computed(() => {
  const t = userAuthStore?.user?.type
  return t === 'super' || t === 'admin'
})

const hasMultipleAccounts = computed(() => {
  const u = userAuthStore?.user
  // Agency staff are excluded: they belong to many companies by definition, and
  // the agency switcher already moves between them.
  return u?.type === 'user' && !u?.agencyId && (u?.companies?.length || 0) > 1
})

// Mobile user menu state
const isMobileUserMenuOpen = ref(false)
const toggleMobileUserMenu = () => {
  isMobileUserMenuOpen.value = !isMobileUserMenuOpen.value
}
const closeMobileUserMenu = () => {
  isMobileUserMenuOpen.value = false
}

// Close on outside click
const handleOutsideClick = (e) => {
  if (isMobileUserMenuOpen.value && !e.target.closest('.mobile-user-menu')) {
    closeMobileUserMenu()
  }
}
onMounted(() => {
  document.addEventListener('click', handleOutsideClick)
  emit('navbar-mounted')
})
onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick)
})
</script>

<style lang="scss" scoped>
.navbar {
  border-bottom: 1px solid #dbdbdb;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);

  /* Agency portal: a dark navbar so the area reads as somewhere else entirely,
     not another company portal. Paired with the square brandmark — the full
     wordmark competes with a dark ground at this size. */
  &.is-agency-nav {
    background: linear-gradient(115deg, #1d2742 0%, #141b31 100%);

    .navbar-item,
    .navbar-link,
    :deep(.navbar-item),
    :deep(.navbar-link) {
      color: #fff;
    }

    :deep(.navbar-item:hover),
    :deep(.navbar-item.is-active),
    :deep(.navbar-link:hover) {
      background-color: rgba(255, 255, 255, 0.09);
      color: #fff;
    }

    /* The brand color is Vysual's here, so active items still read as accent */
    :deep(.navbar-item.is-active .icon),
    :deep(.navbar-item .has-text-primary) {
      color: var(--bulma-primary) !important;
    }

    /* Right-hand icon cluster: duotone marks lose their secondary layer on a
       dark ground, so lift both layers rather than only recoloring the primary. */
    :deep(.navbar-end) {
      .navbar-icon,
      .navbar-icon-wrapper .icon {
        color: #fff;
        --fa-secondary-opacity: 0.65;
      }

      .navbar-icon-wrapper {
        border-radius: 6px;
        transition: background-color 0.15s ease;

        &:hover {
          background-color: rgba(255, 255, 255, 0.12);
        }
      }

      /* Keep the semantic colors, just bright enough to read on dark */
      .has-text-warning {
        color: #ffd66b !important;
      }
      .has-text-danger {
        color: #ff7a7a !important;
      }
      .has-text-success {
        color: #7ee0b8 !important;
      }
      .has-text-primary {
        color: #8ab8ff !important;
      }

      .navbar-avatar-img {
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
      }
    }

    /* Dropdown panels stay light — they're menus, not part of the dark surface */
    :deep(.dropdown-content) {
      color: initial;

      .navbar-item,
      .dropdown-item {
        color: initial;
      }

      .icon {
        --fa-secondary-opacity: 0.4;
      }
    }

  }

  /* Brandmark lockup, shared by both staff portals. Neither is scoped to a
     company, so they run the square mark plus a typeset mode label instead of a
     company logo. Colors are the only per-portal difference. */
  &.has-brand-mark {
    .logo-image {
      // The brandmark SVG carries internal whitespace, so it renders smaller
      // than its box — this is roughly 1.3x the standard logo to match the
      // optical size of the mark inside the full vysual lockup.
      max-height: 64px;

      // ...but the navbar sizes to its tallest child, so at 64px it made the
      // bar 24px taller than every other one. That same internal padding means
      // the mark can overflow its layout box without touching anything, so pull
      // the box back to the standard 40px and let it render large.
      margin-top: -12px;
      margin-bottom: -12px;
    }

    /* No left gap: the brandmark SVG carries its own internal whitespace, so any
       margin here reads as double spacing. The rule on the RIGHT closes the
       lockup and separates it from the nav items. */
    .mode-marker {
      margin-left: 0;
      padding-right: 1rem;
      border-right: 1px solid rgba(10, 10, 10, 0.14);
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: rgba(10, 10, 10, 0.55);
      white-space: nowrap;
    }

    /* Bulma paints a light bg on :focus/:focus-within too, which stays after
       the click navigates — so kill all three, not just :hover. */
    .navbar-brand .navbar-item {
      &:hover,
      &:focus,
      &:focus-within {
        background-color: transparent;
      }
    }

    /* Nested here rather than up in .is-agency-nav so it lands after the
       light-ground defaults it has to beat. */
    &.is-agency-nav .mode-marker {
      border-right-color: rgba(255, 255, 255, 0.22);
      color: rgba(255, 255, 255, 0.72);
    }
  }

  .logo-image {
    max-height: 40px;
    width: auto;
    object-fit: contain;
    transition: opacity 0.2s ease;

    &:not([src]) {
      opacity: 0;
    }

    &[src] {
      opacity: 1;
    }
  }

  .navbar-item {
    gap: 0.5rem;
    display: flex;
    align-items: center;

    object {
      pointer-events: none;
    }
  }

  // Use :deep() so these styles penetrate into child nav components
  .navbar-menu {
    :deep(.navbar-item) {
      &.is-active:not(.settings-dropdown):not(.has-dropdown) {
        background-color: transparent;

        .icon,
        span {
          color: var(--bulma-primary) !important;
        }
      }

      &:not(.has-dropdown).is-active {
        background-color: transparent;

        .icon,
        span {
          color: var(--bulma-primary) !important;
        }
      }

      &:hover {
        background-color: transparent;
        color: var(--bulma-primary);

        .icon {
          color: var(--bulma-primary);
        }
      }
    }
  }

  .navbar-dropdown {
    border-top: 1px solid #dbdbdb;
  }

  .navbar-end {
    .navbar-item {
      &:hover {
        background-color: transparent;
        color: var(--bulma-primary);
      }
      cursor: pointer;
    }
  }
}

.field.is-grouped {
  gap: 0.5rem;
  align-items: center;
}

.control {
  margin: 0 !important;
}

// Mobile navbar adjustments
@media screen and (max-width: 1023px) {
  .navbar {
    padding: 0.25rem 0;
    min-height: auto;

    .container {
      min-height: auto;
      padding: 0 1rem;
    }

    .navbar-brand-mobile {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      min-height: auto;

      .navbar-item {
        padding: 0.125rem 0.5rem;
      }

      .logo-image {
        max-height: 36px;

        &.is-svg,
        &[src*=".svg"] {
          height: 36px;
        }
      }

      .mobile-user-menu {
        display: flex;
        align-items: center;
        padding-right: 0.5rem;

        .dropdown {
          .dropdown-trigger {
            cursor: pointer;
          }

          .dropdown-menu {
            min-width: 220px;
            padding-top: 0.5rem;

            .dropdown-content {
              padding: 0.5rem;

              .dropdown-item {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                padding: 0.5rem 0.75rem;
                border-radius: 6px;
                cursor: pointer;
                font-size: 0.875rem;

                &:hover {
                  background: var(--bulma-scheme-main-bis);
                }
              }

              .dropdown-divider {
                margin: 0.375rem 0;
              }
            }
          }
        }

        .mobile-avatar-wrapper {
          position: relative;
          display: flex;
          align-items: center;

          .avatar-badge {
            position: absolute;
            top: -4px;
            right: -6px;
            min-width: 16px;
            height: 16px;
            padding: 0 4px;
            border-radius: 8px;
            background: var(--bulma-danger);
            color: white;
            font-size: 0.55rem;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid var(--bulma-white);
            line-height: 1;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 1023px) {
  main {
    padding-top: 36px;
  }
}
</style>
